import { Controller } from "@hotwired/stimulus";
import { get, post } from "@rails/request.js";

// Composes a cron string from the Repeats / day / time pickers.
//
// Mirrors cron_controller.js but drives shadcn selects, which post through a hidden input -- so there is
// no .options collection to walk and visibility toggles a wrapper rather than the field.
export default class extends Controller {
  static targets = ["repeats", "dayOfWeek", "dayOfMonth", "timeOfDay", "cadence", "weeklyWrapper",
                    "monthlyWrapper", "timezone", "nextSend", "overdueBadge", "enabled"];
  static values = {
    confirm: String,
    previewUrl: String,
    sendNowUrl: String,
    enabled: { type: Boolean, default: true },
  };

  connect() {
    // Also mounted on forms that carry no schedule pickers, purely for #submit: a missing target here
    // would throw and leave submit unbound.
    if (this.hasRepeatsTarget) this.updateFields();
  }

  get unsavedChanges() {
    return this.application.getControllerForElementAndIdentifier(this.element, "ui--unsaved-changes");
  }

  // Saving navigates to the schedules list, so the form submits natively rather than through fetch. The
  // dirty flag is cleared first or the unload guard would challenge the navigation the save just caused.
  submit(event) {
    if (this.hasConfirmValue && !window.confirm(this.confirmValue)) {
      event.preventDefault();
      return;
    }

    this.unsavedChanges?.clearDirty();
  }

  // A plain button, not a button_to: it sits beside the recipients it mails, which is inside the schedule
  // form, and a nested <form> is invalid HTML the browser drops. Posts the URL directly instead.
  async sendNow(event) {
    event.preventDefault();
    // Each click enqueues an independent job that mails every recipient, so a double click sends the
    // report twice. Disabled for the whole round trip rather than debounced: the button is replaced by
    // the streamed response on success, and re-enabled here only if it is still on the page.
    const button = event.currentTarget;
    if (button.disabled) return;

    // The LIVE form, not the saved record -- a test checks what you are about to configure.
    const body = new FormData(this.element.closest("form"));
    // Its FormData carries _method=patch, which Rails honours over the POST verb and routes to #update
    // with id="send_now" -- a 404, no send, no notice.
    body.delete("_method");

    const recipients = body.getAll("subscription[recipients][]").filter((r) => r.trim() !== "");
    // Empty falls through to the server, which answers "Add a recipient before sending a test."
    if (recipients.length > 0 &&
        !window.confirm(`Send a test of this dashboard to ${recipients.join(", ")} now?`)) {
      return;
    }

    button.disabled = true;
    try {
      // Streamed into #subscription-send-result, NOT navigated. Following a redirect here rendered the
      // edit page invisibly and consumed its one-shot flash, so the second GET that displayed it showed
      // neither the confirmation nor the validation error -- and reloading at all would throw away the
      // unsaved settings this test was sent to check.
      await post(this.sendNowUrlValue, { body, responseKind: "turbo-stream" });
    } finally {
      button.disabled = false;
    }
  }

  onChangeRepeat() {
    this.updateFields();
    this.onChangeSchedule();
  }

  onChangeSchedule() {
    this.dropOverdueBadge();
    this.calculateCadence();
  }

  // The badge describes the SAVED due_at, so it must not sit beside a preview of a rescheduled send. Only
  // on a user-initiated change: connect() runs the same preview chain, and dropping it there would clear
  // the badge of an untouched overdue schedule moments after the dialog opens.
  dropOverdueBadge() {
    if (this.hasOverdueBadgeTarget) this.overdueBadgeTarget.remove();
  }

  updateFields() {
    const repeats = this.repeatsTarget.value;
    this.weeklyWrapperTarget.classList.toggle("hidden", repeats !== "weekly");
    this.monthlyWrapperTarget.classList.toggle("hidden", !["monthly", "quarterly"].includes(repeats));
    this.calculateCadence();
  }

  calculateCadence() {
    const [hour, minuteInput] = this.timeOfDayTarget.value.split(":");
    const minute = minuteInput === "30" ? "30" : "0";
    const repeats = this.repeatsTarget.value;

    const dayOfMonth = ["monthly", "quarterly"].includes(repeats) ? this.dayOfMonthTarget.value : "*";
    const monthOfYear = repeats === "quarterly" ? "*/3" : "*";
    const dayOfWeek = repeats === "weekly" ? this.dayOfWeekTarget.value : "*";

    this.cadenceTarget.value = `${minute} ${hour} ${dayOfMonth} ${monthOfYear} ${dayOfWeek}`;
    this.updateNextSend();
  }

  get timezone() {
    return this.hasTimezoneTarget ? this.timezoneTarget.value : null;
  }

  get enabled() {
    return this.hasEnabledTarget ? this.enabledTarget.value === "true" : this.enabledValue;
  }

  // The server owns next_due_at (CronCalc plus the zone's DST rules), so the preview asks it rather than
  // reimplementing that here -- two implementations of a schedule would drift.
  async updateNextSend() {
    if (!this.hasNextSendTarget) return;

    // Every picker change fires a request, and they can land out of order -- a slow earlier one would
    // otherwise overwrite the preview for the selection now on screen. Only the newest request is
    // allowed to write, so a stale reply is discarded rather than shown.
    const token = (this.previewToken = (this.previewToken || 0) + 1);

    const response = await get(this.previewUrlValue, {
      query: { cadence: this.cadenceTarget.value, timezone: this.timezone, enabled: this.enabled },
      responseKind: "json",
    });

    const body = response.ok ? await response.json : null;
    if (token !== this.previewToken) return;

    // The server answers with its own wording for a paused schedule, so this covers only a failed request.
    this.nextSendTarget.textContent = body?.next_send || "Next send unavailable for this schedule";
  }
}
